// NIGHT-DIRECTSTAFF-2026-10-19 §3: exam review for Matt -- a finished attempt question by question (the portal's
// /api/exam-review; the answer key never leaves the portal), and the "Send result" message.
//
// The portal decides who may see a review by reading the attempt WITH THE STAFF MEMBER'S OWN SESSION TOKEN under the
// exam tables' row-level security (allowlisted staff only) -- so this sends the CRM's signed-in access token, nothing else.
import { firstName } from './sendLink';
import { EXAM_BY_ID, PASS_PCT } from './exams';

export const REVIEW_ERRORS = {
  'review:sign_in': 'Sign in again to see the review.',
  'review:not_allowed': "Your account can't see this exam.",
  'review:not_finished': "This attempt isn't finished yet.",
  'review:unknown_exam': "This exam isn't one the portal knows any more.",
  'review:unavailable': "The review isn't available right now. Try again in a minute.",
};

/** -> the review, or throws Error(plain words). */
export async function fetchReview({ supabase, baseUrl, attemptId, fetchFn = (...a) => fetch(...a) }) {
  let token = '';
  try { const { data } = await supabase.auth.getSession(); token = (data && data.session && data.session.access_token) || ''; } catch { token = ''; }
  if (!token) throw new Error(REVIEW_ERRORS['review:sign_in']);
  let res;
  try {
    res = await fetchFn(`${String(baseUrl || '').replace(/\/$/, '')}/api/exam-review`, {
      method: 'POST', headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` }, body: JSON.stringify({ attempt_id: attemptId }) });
  } catch { throw new Error(REVIEW_ERRORS['review:unavailable']); }
  const out = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(REVIEW_ERRORS[out && out.error] || (res.status === 404 ? "The review isn't switched on yet (the portal update)." : REVIEW_ERRORS['review:unavailable']));
  return out;
}

const short = (examId) => (EXAM_BY_ID[examId] ? EXAM_BY_ID[examId].short : 'competency');
const when = (d) => d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

/** The sections to review after a fail: under the pass line, weakest first, at most three. */
export function weakSections(review, n = 3) {
  return (review.sections || []).filter((s) => s.pct < (review.passPct || PASS_PCT)).sort((a, b) => a.pct - b.pct || a.name.localeCompare(b.name)).slice(0, n).map((s) => s.name);
}

/**
 * The plain, kind message. Passed: the next step is onboarding. Failed: what to review, and that they can retake it once
 * with the new link (the portal opens a retake 24 hours after the last attempt; the message says when, if that's still ahead).
 */
export function resultMessage({ name, review, link = '', now = Date.now() }) {
  const hi = firstName(name) ? `Hi ${firstName(name)},` : 'Hi,';
  const exam = `ProTherapy ${short(review.exam_id)} exam`;
  if (review.passed) {
    return `${hi} good news: you passed the ${exam} with ${review.pct}%. The next step is your onboarding packet. If you already have its link, `
      + "pick up where you left off; if not, we'll send it to you. Reply here with any questions.";
  }
  const weak = weakSections(review);
  const opens = review.submitted_at ? new Date(new Date(review.submitted_at).getTime() + 24 * 3600e3) : null;
  const later = opens && opens.getTime() > now ? ` It opens ${when(opens)}.` : '';
  return `${hi} thank you for taking the ${exam}. You scored ${review.pct}% and the pass mark is ${review.passPct || PASS_PCT}%, so it isn't a pass yet.`
    + (weak.length ? ` Worth a look before you try again: ${weak.join('; ')}.` : '')
    + ` You can retake it once with this link: ${link}.${later} Reply here with any questions.`;
}

/** §3 (10-21): the message after the SECOND fail -- both attempts used, no link; staff decide on another attempt. */
export function lockedMessage({ name, review }) {
  const hi = firstName(name) ? `Hi ${firstName(name)},` : 'Hi,';
  return `${hi} thank you for taking the ProTherapy ${short(review.exam_id)} exam again. You scored ${review.pct}% and the pass mark is ${review.passPct || PASS_PCT}%. `
    + "You've used both attempts, so we'll be in touch about next steps. Reply here with any questions.";
}
/** §3 (10-21): the message with the link Allow another attempt makes. */
export function anotherAttemptMessage({ name, examId, link }) {
  const hi = firstName(name) ? `Hi ${firstName(name)},` : 'Hi,';
  return `${hi} here's a new link for one more try at the ProTherapy ${short(examId)} exam: ${link}. It's the same exam and the same rules. Reply here with any questions.`;
}
